import React, { useCallback, memo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  useTheme,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
  Fade,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import StarIcon from '@mui/icons-material/Star';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { Formik, Form, FormikProps } from 'formik';
import { uploadBusinessLogo } from '../../../services/storage';
import { BusinessListingForm } from '../../../data/types/application';
import * as Yup from 'yup';
import { Business } from '../../../data/types/domain';
import { BusinessField } from './BusinessField';  // Move BusinessField to its own file
import Dropzone from 'react-dropzone';

// Define validation schema
const validationSchema = Yup.object().shape({
  business: Yup.object().shape({
    name: Yup.string().required('Business name is required'),
    description: Yup.string().required('Description is required'),
    contact: Yup.string().nullable(),
    email: Yup.string().email('Invalid email format').nullable(),
    address: Yup.string().nullable(),
    postcode: Yup.string().nullable(),
    owner: Yup.string().nullable(),
    user_owner: Yup.string().nullable(),
    website: Yup.string().url('Invalid URL format').nullable(),
    facebook: Yup.string().url('Invalid Facebook URL').nullable(),
    instagram: Yup.string().url('Invalid Instagram URL').nullable(),
    twitter: Yup.string().url('Invalid Twitter URL').nullable(),
    youtube: Yup.string().url('Invalid YouTube URL').nullable(),
    google_rating: Yup.number().transform((value) => {
      if (value === '' || value === null || value === undefined) return 0;
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    }).nullable(),
    google_reviews_count: Yup.number().transform((value) => {
      if (value === '' || value === null || value === undefined) return 0;
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    }).nullable(),
    google_reviews_url: Yup.string().url('Invalid Google Reviews URL').nullable(),
    trustpilot_rating: Yup.number().transform((value) => {
      if (value === '' || value === null || value === undefined) return 0;
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    }).nullable(),
    trustpilot_reviews_count: Yup.number().transform((value) => {
      if (value === '' || value === null || value === undefined) return 0;
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    }).nullable(),
    trustpilot_reviews_url: Yup.string().url('Invalid Trustpilot Reviews URL').nullable(),
  }).test('at-least-one-review', 'Either Google or Trustpilot review is required', function(value) {
    const hasGoogleReview = value.google_rating != null && value.google_reviews_count != null;
    const hasTrustpilotReview = value.trustpilot_rating != null && value.trustpilot_reviews_count != null;
    return hasGoogleReview || hasTrustpilotReview;
  }),
});

export interface BusinessDetailsStepRef {
  isValid: () => Promise<boolean>;
  getErrors: () => Promise<string[]>;
  getData: () => Promise<any>;
}

interface BusinessDetailsStepProps {
  initialData?: Partial<Omit<Business, 'id' | 'created_at' | 'updated_at'>>;
}

export const BusinessDetailsStep = forwardRef<BusinessDetailsStepRef, BusinessDetailsStepProps>(({ initialData }, ref) => {
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const formikRef = React.useRef<FormikProps<any>>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadError(null);
      const logoUrl = await uploadBusinessLogo(file, formikRef.current?.values.business.name);
      formikRef.current?.setFieldValue('business.logo', logoUrl);
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setUploadError(error.message || 'Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
    }
  }, []);

  const initialValues = {
    business: initialData || {
      name: '',
      description: '',
      contact: '',
      address: '',
      postcode: '',
      email: '',
      owner: '',
      user_owner: null,
      logo: '',
      website: '',
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: '',
      google_rating: null,
      google_reviews_count: null,
      google_reviews_url: '',
      trustpilot_rating: null,
      trustpilot_reviews_count: null,
      trustpilot_reviews_url: '',
    }
  };

  useImperativeHandle(ref, () => ({
    isValid: async () => {
      try {
        await validationSchema.validate({ business: formikRef.current?.values.business }, { abortEarly: false });
        return true;
      } catch (err) {
        return false;
      }
    },
    getErrors: async () => {
      try {
        await validationSchema.validate({ business: formikRef.current?.values.business }, { abortEarly: false });
        return [];
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          return err.errors;
        }
        return ['An unknown error occurred'];
      }
    },
    getData: async () => {
      return formikRef.current?.values.business;
    }
  }));

  const FormSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Card sx={{ 
      mb: 3,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      borderRadius: 2,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      }
    }}>
      <CardContent>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 600,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );

  const handleRatingChange = useCallback((name: string, value: string) => {
    const numValue = value === '' ? null : Number(value);
    formikRef.current?.setFieldValue(`business.${name}`, numValue);
  }, []);

  const handleReviewCountChange = useCallback((name: string, value: string) => {
    const numValue = value === '' ? 0 : Number(value);
    formikRef.current?.setFieldValue(`business.${name}`, numValue);
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
      innerRef={formikRef}
    >
      {(formik) => (
        <Form>
          <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
            {/* Logo Upload Section */}
            <FormSection title="Business Logo">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {uploading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : formik.values.business?.logo ? (
                    <Box sx={{ width: '100%', position: 'relative' }}>
                      <img
                        src={formik.values.business.logo}
                        alt="Business Logo"
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                        }}
                      />
                      <Tooltip title="Remove Logo">
                        <IconButton
                          onClick={() => formik.setFieldValue('business.logo', '')}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Dropzone
                      onDrop={onDrop}
                      accept={{
                        'image/*': ['.jpeg', '.jpg', '.png']
                      }}
                      maxSize={5 * 1024 * 1024}
                    >
                      {({getRootProps, getInputProps, isDragActive}: {
                        getRootProps: () => any;
                        getInputProps: () => any;
                        isDragActive: boolean;
                      }) => (
                        <div 
                          {...getRootProps()} 
                          style={{
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <input {...getInputProps()} />
                          <Paper
                            sx={{
                              p: 3,
                              border: '2px dashed',
                              borderColor: isDragActive ? 'primary.dark' : 'primary.main',
                              backgroundColor: isDragActive ? 'action.hover' : 'background.default',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: theme.palette.primary.dark,
                                backgroundColor: 'action.hover',
                              },
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body1" color="textSecondary" align="center">
                              {isDragActive ? 
                                'Drop the image here...' : 
                                'Click or drag and drop to upload your business logo'
                              }
                            </Typography>
                            <Typography variant="caption" color="textSecondary" align="center">
                              Supported formats: PNG, JPG, JPEG (max 5MB)
                            </Typography>
                          </Paper>
                        </div>
                      )}
                    </Dropzone>
                  )}
                  {uploadError && (
                    <Typography 
                      color="error" 
                      align="center" 
                      sx={{ mt: 2 }}
                    >
                      {uploadError}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </FormSection>

            {/* Basic Information */}
            <FormSection title="Basic Information">
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Business Name"
                    name="name"
                    required
                    icon={<BusinessIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BusinessField
                    label="Description"
                    name="description"
                    multiline
                    rows={4}
                    required
                    icon={<DescriptionIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Contact Information */}
            <FormSection title="Contact Information">
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Contact Person"
                    name="contact"
                    icon={<PersonIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Email"
                    name="email"
                    type="email"
                    icon={<EmailIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Owner Name"
                    name="owner"
                    icon={<PersonIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Address */}
            <FormSection title="Address">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <BusinessField
                    label="Address"
                    name="address"
                    icon={<LocationOnIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Postcode"
                    name="postcode"
                    icon={<LocationOnIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Online Presence */}
            <FormSection title="Online Presence">
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Website"
                    name="website"
                    icon={<LanguageIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Facebook"
                    name="facebook"
                    icon={<FacebookIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Instagram"
                    name="instagram"
                    icon={<InstagramIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Twitter"
                    name="twitter"
                    icon={<TwitterIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="YouTube"
                    name="youtube"
                    icon={<YouTubeIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Ratings & Reviews */}
            <FormSection title="Ratings & Reviews">
              <Grid container spacing={3}>
                {/* Google Reviews */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon fontSize="small" /> Google Reviews
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Google Rating"
                    name="google_rating"
                    type="number"
                    value={formik.values.business.google_rating ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRatingChange('google_rating', e.target.value)}
                    icon={<StarIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Google Reviews Count"
                    name="google_reviews_count"
                    value={formik.values.business.google_reviews_count?.toString() ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleReviewCountChange('google_reviews_count', e.target.value)}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BusinessField
                    label="Google Reviews URL"
                    name="google_reviews_url"
                    icon={<LanguageIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                {/* Trustpilot Reviews */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon fontSize="small" /> Trustpilot Reviews
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Trustpilot Rating"
                    name="trustpilot_rating"
                    type="number"
                    value={formik.values.business.trustpilot_rating}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRatingChange('trustpilot_rating', e.target.value)}
                    icon={<StarIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BusinessField
                    label="Trustpilot Reviews Count"
                    name="trustpilot_reviews_count"
                    value={formik.values.business.trustpilot_reviews_count?.toString() ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleReviewCountChange('trustpilot_reviews_count', e.target.value)}
                    formik={formik}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BusinessField
                    label="Trustpilot Reviews URL"
                    name="trustpilot_reviews_url"
                    icon={<LanguageIcon color="primary" />}
                    formik={formik}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </Box>
        </Form>
      )}
    </Formik>
  );
}); 